import queryString from 'query-string';
import { memo, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { isMcatCourse } from 'my-core/course-utils';
import { onlineCourseCurriculumSearched } from 'my-core/gtm-events';
import { useCurrentUser, useDebounce, useSiteTourStep } from 'my-core/hooks';
import { useOnlineCourseTokens } from 'my-core/in-course-experience-hooks';
import { canPreviewOnlineCourse } from 'my-core/online-course-utils';
import {
  studentCoursePageAssessmentRelativePath,
  studentCoursePageGradeBoostersRelativePath,
  studentCoursePageOnDemandRelativePath,
  studentCoursePagePath,
} from 'my-core/routes';
import { getUserFirstName } from 'my-core/user-utils';

import CourseCurriculum from 'my-components/CourseCurriculum';
import InCourseExperienceHeaderWrapper from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceHeaderWrapper';
import { DRAWER_TRANSITION_DURATION } from 'my-components/OnlineCourseLayout';
import DropDownButton from 'my-elements/DropDownButton';
import Label from 'my-elements/Label';

import {
  BOOKMARKS_STEP,
  COURSE_SWITCH_STEP,
  EXAM_BREAKDOWN_STEP,
  GRADE_BOOSTER_PAGES_STEP,
  // LESSON_EXPERIENCE_UPDATES_STEP,
  OVERVIEW_STEP,
  // ASSESSMENT_OVERVIEW_STEP,
  SEARCH_STEP,
  TOUR_ID,
} from './student-course-page-tour';

import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import {
  faAngleRight,
  faAnglesLeft,
  faBookmark,
  faClose,
  faCommentExclamation,
  faHome,
  faRocket,
  faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const classes = {
  root: {
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: ({ spacing }) => ({
    flex: '0 0 auto',
    justifyContent: 'space-between',
    paddingLeft: spacing(2),
  }),
  content: ({ spacing }) => ({
    paddingTop: spacing(2),
    flex: '1 1 0',
    minHeight: 0,
    overflow: 'auto',
  }),
};

export default function StudentCoursePageSidebar({
  activeComponent,
  assessments,
  canViewAll,
  classPass,
  classPassContentMap,
  course,
  currentUser,
  drawerOpen,
  fetching,
  onClose,
  // onlineCourses,
  onlineCourseUser,
  pathMatchData,
  practiceMode,
  practiceModeSectionResults,
  shouldCloseDrawerOnNav,
  studySessions,
}) {
  const [displayMode, setDisplayMode] = useState();
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 200);
  useEffect(() => {
    if (debouncedFilter?.length > 2) {
      onlineCourseCurriculumSearched(debouncedFilter, classPass, course);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilter]);

  const handleLinkClick = shouldCloseDrawerOnNav ? onClose : undefined;
  const assessmentsWithContentsCount = assessments.filter(a => a.contents).length;
  const hasAssessmentContents = !!assessmentsWithContentsCount;
  const assessmentViewOverride = pathMatchData.pathname === studentCoursePageOnDemandRelativePath();
  const [assessmentViewState, setAssessmentView] = useState(assessmentViewOverride ? false : undefined);
  const assessmentView =
    typeof assessmentViewState === 'boolean' ? assessmentViewState
    : assessmentViewOverride ? false
    : hasAssessmentContents && assessments.length === assessmentsWithContentsCount && assessmentsWithContentsCount > 1;
  const showExamViewToggle = !!hasAssessmentContents;
  const footerHeight = showExamViewToggle ? 100 : 60;
  const { tokenPurchases } = useOnlineCourseTokens(currentUser.id, classPass?.id);
  const canPreviewClassPass = canPreviewOnlineCourse(classPass, canViewAll);
  const overviewNavItemId = useSiteTourStep({ step: OVERVIEW_STEP, tourId: TOUR_ID });
  const examBreakdownStepId = useSiteTourStep({
    step: EXAM_BREAKDOWN_STEP,
    tourId: TOUR_ID,
    disabled: !assessmentView,
  });
  const boosterPagesId = useSiteTourStep({
    step: GRADE_BOOSTER_PAGES_STEP,
    tourId: TOUR_ID,
    disabled: !studySessions.length, // || onlineCourses.length),
  });
  const searchBtnId = useSiteTourStep({ step: SEARCH_STEP, tourId: TOUR_ID, disabled: !classPassContentMap });
  const bookmarkBtnId = useSiteTourStep({ step: BOOKMARKS_STEP, tourId: TOUR_ID, disabled: !classPassContentMap });

  if (displayMode === 'search') return renderSearchContents();
  if (displayMode === 'bookmarks') return renderBookmarksContents();

  return (
    <div css={classes.root}>
      <InCourseExperienceHeaderWrapper css={classes.header}>
        <div css={{ flex: '1 1 0', minWidth: 0 }}>
          <CourseSelectButton
            closeAfterTourStep={shouldCloseDrawerOnNav}
            course={course}
            drawerOpen={drawerOpen}
            fetching={fetching}
            onToggleDrawerOpen={onClose}
          />
          <Typography component="p" sx={{ color: 'text.secondary' }} variant="overline">
            {course?.school?.short_name ||
              course?.school?.name ||
              (fetching && <Skeleton variant="text" width={100} />)}
          </Typography>
        </div>
        <div css={{ flex: '0 0 auto' }}>
          {!!classPassContentMap && (
            <>
              <IconButton id={searchBtnId} onClick={() => setDisplayMode('search')}>
                <FontAwesomeIcon icon={faSearch} size="xs" />
              </IconButton>
              <IconButton id={bookmarkBtnId} onClick={() => setDisplayMode('bookmarks')}>
                <FontAwesomeIcon icon={faBookmark} size="xs" />
              </IconButton>
            </>
          )}
          <IconButton onClick={onClose}>
            <FontAwesomeIcon icon={faAnglesLeft} size="xs" />
          </IconButton>
        </div>
      </InCourseExperienceHeaderWrapper>
      <div css={classes.content} style={{ paddingBottom: footerHeight }}>
        <NavigationItem
          active={pathMatchData.pathname === ''}
          buttonProps={{
            id: overviewNavItemId,
            component: Link,
            nativeButton: false,
            to: '..',
            onClick: handleLinkClick,
          }}
          icon={faHome}
          label="Course Overview"
        />
        {/* <NavigationItem icon={faBookmark} label="Bookmarks" /> */}
        {!!studySessions.length /*|| onlineCourses.length)*/ && (
          <NavigationItem
            active={pathMatchData.pathname === studentCoursePageGradeBoostersRelativePath()}
            buttonProps={{
              id: boosterPagesId,
              component: Link,
              nativeButton: false,
              to: `../${studentCoursePageGradeBoostersRelativePath()}`,
              onClick: handleLinkClick,
            }}
            icon={faRocket}
            label="Grade Boosters"
            tag={
              studySessions.some(ss => ss.purchase_type === 'standard' && ss.price && ss.regular_price > ss.price) &&
              'sale'
            }
          />
        )}
        {!!(hasAssessmentContents || classPassContentMap || fetching) && <NavSubheader label="Lessons & Practice" />}
        {assessmentView ?
          <div id={examBreakdownStepId}>
            {assessments
              .filter(a => a.contents)
              .map(a => (
                <AssessmentNavigationSection
                  key={a.id}
                  activeComponent={activeComponent}
                  assessment={a}
                  canPreviewAll={canPreviewClassPass}
                  canViewAll={canViewAll}
                  onlineCourseUser={onlineCourseUser}
                  onLinkClick={handleLinkClick}
                  pathMatchData={pathMatchData}
                  practiceMode={practiceMode}
                  practiceModeSectionQuestionCounts={classPass.practice_mode_section_question_counts}
                  practiceModeSectionResults={practiceModeSectionResults}
                  tokenPurchases={tokenPurchases}
                />
              ))}
          </div>
        : classPassContentMap ?
          <CourseCurriculum
            activeComponent={activeComponent}
            ActivityProps={{
              css: { '&.activity_topLevel': { borderBottom: 'unset' } },
              topLevelClassName: 'activity_topLevel',
            }}
            basePath={`../${studentCoursePageOnDemandRelativePath()}`}
            canPreviewAll={canPreviewClassPass}
            canViewAll={canViewAll}
            ChapterProps={{
              headerClassName: 'chapter-header',
              css: ({ palette }) => ({ '& .chapter-header': { backgroundColor: palette.background.paper } }),
            }}
            displayMeta
            onlineCourseContent={classPassContentMap}
            onlineCourseUser={onlineCourseUser}
            onLinkClick={handleLinkClick}
            practiceMode={practiceMode}
            practiceModeSectionQuestionCounts={classPass.practice_mode_section_question_counts}
            practiceModeSectionResults={practiceModeSectionResults}
            tokenPurchases={tokenPurchases}
          />
        : fetching &&
          [...Array(2)].map((_, idx) => (
            <AssessmentNavigationSection
              key={idx}
              fetching
              onLinkClick={handleLinkClick}
              pathMatchData={pathMatchData}
            />
          ))
        }
      </div>
      <div
        css={({ palette, spacing }) => ({
          height: footerHeight,
          zIndex: 1,
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          backgroundColor: palette.background.default,
          padding: spacing(0, 3),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: spacing(2),

          '& > .control': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        })}
      >
        {showExamViewToggle && (
          <div className="control">
            <Typography variant="subtitle1">Exam View</Typography>
            <Switch checked={assessmentView} onChange={e => setAssessmentView(e.target.checked)} size="small" />
          </div>
        )}
        <Button
          component="a"
          fullWidth
          href={`https://form.typeform.com/to/s40LIT25#${queryString.stringify({
            email: currentUser.email,
            name: getUserFirstName(currentUser),
            user_id: currentUser.id,
            course_code: course?.code,
            school_name: course?.school?.short_name || course?.school?.name,
            current_url: window.location.href,
          })}`}
          nativeButton={false}
          rel="noreferrer"
          size="small"
          startIcon={<FontAwesomeIcon icon={faCommentExclamation} />}
          target="_blank"
          variant="outlined"
        >
          Leave Feedback
        </Button>
      </div>
    </div>
  );

  function renderSearchContents() {
    return (
      <div css={classes.root}>
        <InCourseExperienceHeaderWrapper css={classes.header}>
          <IconButton onClick={() => setDisplayMode()}>
            <FontAwesomeIcon icon={faClose} size="xs" />
          </IconButton>
          <InputBase
            fullWidth
            inputProps={{ className: classes.searchInput }}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search Curriculum"
            sx={{ backgroundColor: 'background.default', pl: 2, borderRadius: 1 }}
            value={filter}
          />
          <IconButton onClick={onClose}>
            <FontAwesomeIcon icon={faAnglesLeft} size="xs" />
          </IconButton>
        </InCourseExperienceHeaderWrapper>
        <div css={classes.content}>
          <NavSubheader label="Lessons & Practice" />
          <CourseCurriculum
            activeComponent={activeComponent}
            ActivityProps={{
              css: { '&.activity_topLevel': { borderBottom: 'unset' } },
              topLevelClassName: 'activity_topLevel',
            }}
            basePath={`../${studentCoursePageOnDemandRelativePath()}`}
            canPreviewAll={canPreviewClassPass}
            canViewAll={canViewAll}
            ChapterProps={{
              css: ({ palette }) => ({ '& .chapter-header': { backgroundColor: palette.background.paper } }),
              headerClassName: 'chapter-header',
            }}
            displayMeta
            filter={debouncedFilter}
            onlineCourseContent={classPassContentMap}
            onlineCourseUser={onlineCourseUser}
            onLinkClick={handleLinkClick}
            practiceMode={practiceMode}
            practiceModeSectionQuestionCounts={classPass.practice_mode_section_question_counts}
            practiceModeSectionResults={practiceModeSectionResults}
            tokenPurchases={tokenPurchases}
          />
        </div>
      </div>
    );
  }

  function renderBookmarksContents() {
    return (
      <div css={classes.root}>
        <InCourseExperienceHeaderWrapper css={classes.header}>
          <IconButton onClick={() => setDisplayMode()}>
            <FontAwesomeIcon icon={faClose} size="xs" />
          </IconButton>
          <Typography sx={{ flex: 1 }} variant="h6">
            Bookmarks
          </Typography>
          <IconButton onClick={onClose}>
            <FontAwesomeIcon icon={faAnglesLeft} size="xs" />
          </IconButton>
        </InCourseExperienceHeaderWrapper>
        <div css={classes.content}>
          <CourseCurriculum
            activeComponent={activeComponent}
            ActivityProps={{
              css: ({ spacing }) => ({ '&.activity_root': { padding: spacing(1, 2) } }),
              className: 'activity_root',
            }}
            basePath={`../${studentCoursePageOnDemandRelativePath()}`}
            canPreviewAll={canPreviewClassPass}
            canViewAll={canViewAll}
            displayMeta
            onlineCourseContent={classPassContentMap}
            onlineCourseUser={onlineCourseUser}
            onLinkClick={handleLinkClick}
            onlyFaves
            tokenPurchases={tokenPurchases}
          />
        </div>
      </div>
    );
  }
}

const CourseSelectButton = memo(function CourseSelectButton({
  closeAfterTourStep,
  course,
  drawerOpen,
  fetching,
  onToggleDrawerOpen,
}) {
  const [buttonOpen, setButtonOpen] = useState(false);
  const currentUser = useCurrentUser({ includeCourses: true });
  const otherCourses = currentUser.user_courses
    .filter(uc => !uc.is_past && uc.course_id !== course?.id && !isMcatCourse(uc.course))
    .map(uc => uc.course);
  const label = (
    <Typography noWrap variant="h6">
      {course?.code || (fetching && <Skeleton variant="text" width={100} />)}
    </Typography>
  );
  const switchStep = useMemo(
    () => ({
      ...COURSE_SWITCH_STEP,
      onBefore: cb => {
        setButtonOpen(true);
        if (closeAfterTourStep && !drawerOpen) {
          onToggleDrawerOpen();
          setTimeout(cb, DRAWER_TRANSITION_DURATION + 100);
        } else {
          cb();
        }
      },
      onAfter: cb => {
        setButtonOpen(false);
        if (closeAfterTourStep && drawerOpen) {
          onToggleDrawerOpen();
          setTimeout(cb, DRAWER_TRANSITION_DURATION + 100);
        } else {
          cb();
        }
      },
      onReverseAfter: cb => {
        setButtonOpen(false);
        cb();
      },
    }),
    [closeAfterTourStep, drawerOpen, onToggleDrawerOpen],
  );
  const switchId = useSiteTourStep({ step: switchStep, tourId: TOUR_ID, disabled: !otherCourses.length });
  if (!otherCourses.length) return label;
  return (
    <DropDownButton
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      button={
        <ButtonBase
          id={switchId}
          sx={{
            maxWidth: '100%',
            px: 1,
            ml: '-9px',
            display: 'flex',
            gap: 1,
            borderRadius: 0.5,
            border: '1px solid transparent',
            ...(buttonOpen && {
              backgroundColor: 'background.paper1',
              borderColor: 'divider',
            }),
            '& > svg': buttonOpen ? { transform: 'rotate(180deg)' } : { opacity: 0 },
            '&:hover': {
              backgroundColor: 'background.default',
              borderColor: 'divider',
              '& > svg': { opacity: 1 },
            },
          }}
        >
          {label}
          <FontAwesomeIcon icon={faCaretDown} />
        </ButtonBase>
      }
      marginThreshold={0}
      onToggleOpen={setButtonOpen}
      slotProps={{ paper: { style: { maxHeight: 400 } } }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
    >
      {otherCourses.map(course => (
        <ListItemButton key={course.id} component={Link} dense nativeButton={false} to={studentCoursePagePath(course)}>
          <ListItemText
            primary={course.code}
            secondary={course.name}
            slotProps={{
              primary: { variant: 'subtitle2' },
            }}
          />
        </ListItemButton>
      ))}
    </DropDownButton>
  );
});

function NavigationItem({ active, buttonProps, icon, indent, label, labelVariant = 'subtitle1', tag }) {
  return (
    <ButtonBase
      {...buttonProps}
      css={[
        buttonProps?.css,
        ({ spacing }) => ({
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-start',
          alignItems: 'center',
          textAlign: 'left',
          padding: spacing(1.5, 2),
          '& > svg': { marginRight: spacing(1) },
          '& > .tag': {
            marginLeft: 'auto',
            background: 'linear-gradient(90deg, #3EC1F3 0%, #9747FF 100%)',
            // background: 'linear-gradient(to right, #9747FF 0%, #FF6576 100%)',
          },
        }),
        indent && (({ spacing }) => ({ paddingLeft: spacing(3) })),
        active &&
          (({ palette }) => ({
            backgroundColor: palette.primary.background,
            color: palette.primary.main,
            '& > svg': { color: 'inherit' },
          })),
      ]}
    >
      {icon && <FontAwesomeIcon fixedWidth icon={icon} />}
      <Typography variant={labelVariant}>{label}</Typography>
      {tag && <Label className="tag" label={tag} />}
    </ButtonBase>
  );
}

function AssessmentNavigationSection({
  activeComponent,
  assessment,
  canPreviewAll,
  canViewAll,
  fetching,
  onlineCourseUser,
  onLinkClick,
  pathMatchData,
  practiceMode,
  practiceModeSectionQuestionCounts,
  practiceModeSectionResults,
  tokenPurchases,
}) {
  const basePath = studentCoursePageAssessmentRelativePath(assessment);
  const basePathTo = `../${basePath}`;
  const active = pathMatchData.pathname === basePath; //.startsWith('assessments') && +pathMatchData.params.id === assessment?.id;
  const [expanded, setExpanded] = useState(active);
  useEffect(() => {
    setExpanded(active);
  }, [active]);
  return (
    <ExpandingNavItemWrapper active={active}>
      <ButtonBase
        css={[
          ({ palette, spacing, transitions }) => ({
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            textAlign: 'left',
            padding: spacing(2),
            '& > svg': {
              marginRight: spacing(1),
              color: palette.text.secondary,
              transition: transitions.create('transform', { duration: transitions.duration.shortest }),
            },
          }),
          expanded && { '& > svg': { transform: 'rotate(90deg)' } },
        ]}
        disabled={!!fetching}
        onClick={() => setExpanded(!expanded)}
      >
        <FontAwesomeIcon fixedWidth icon={faAngleRight} />
        <Typography variant="subtitle1">
          {fetching ?
            <Skeleton width={100} />
          : assessment.name}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <NavigationItem
          active={active && !pathMatchData.params?.['*']}
          buttonProps={{
            component: Link,
            nativeButton: false,
            to: basePathTo,
            onClick: onLinkClick,
            css: ({ palette }) => ({ backgroundColor: palette.background.default }),
          }}
          indent
          label={`${assessment?.name} Overview`}
          labelVariant="subtitle2"
        />
        <NavigationAssessmentContents
          activeComponent={activeComponent}
          assessment={assessment}
          basePath={basePathTo}
          canPreviewAll={canPreviewAll}
          canViewAll={canViewAll}
          fetching={fetching}
          onlineCourseUser={onlineCourseUser}
          onLinkClick={onLinkClick}
          practiceMode={practiceMode}
          practiceModeSectionQuestionCounts={practiceModeSectionQuestionCounts}
          practiceModeSectionResults={practiceModeSectionResults}
          tokenPurchases={tokenPurchases}
        />
      </Collapse>
    </ExpandingNavItemWrapper>
  );
}

function NavigationAssessmentContents({
  activeComponent,
  assessment,
  basePath,
  canPreviewAll,
  canViewAll,
  onlineCourseUser,
  onLinkClick,
  practiceMode,
  practiceModeSectionQuestionCounts,
  practiceModeSectionResults,
  tokenPurchases,
}) {
  return (
    <div>
      {!!assessment.contents && (
        <>
          <CourseCurriculum
            activeComponent={activeComponent}
            basePath={basePath}
            canPreviewAll={canPreviewAll}
            canViewAll={canViewAll}
            displayMeta
            onlineCourseContent={assessment.contents}
            onlineCourseUser={onlineCourseUser}
            onLinkClick={onLinkClick}
            practiceMode={practiceMode}
            practiceModeSectionQuestionCounts={practiceModeSectionQuestionCounts}
            practiceModeSectionResults={practiceModeSectionResults}
            tokenPurchases={tokenPurchases}
            // onSetFavorite={onSetFavorite}
            // eventTrackingProperties={eventTrackingProperties}
          />
        </>
      )}
    </div>
  );
}

function NavSubheader({ icon, label }) {
  return (
    <Typography component="p" sx={{ color: 'text.secondary', px: 2, py: 1.5, '& > svg': { mr: 1 } }} variant="overline">
      {icon && <FontAwesomeIcon fixedWidth icon={icon} />}
      {label}
    </Typography>
  );
}

function ExpandingNavItemWrapper({ active, children }) {
  return (
    <div
      css={[
        {
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: 3,
          },
        },
        active &&
          (({ palette }) => ({
            '&::after': {
              backgroundColor: palette.primary.main,
            },
          })),
      ]}
    >
      {children}
    </div>
  );
}
